'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useRouter } from 'next/navigation';
import { Loading } from '@pickatale/ui-kit';
import { useGetFlagsForUserQuery, GetFlagsForUserQuery } from '@/api/types';
import { RoleFlagOptions, UserInfo } from '@/types';
import { userHasRequiredRole } from '@/utils/roles';
import {
  storeToken,
  retrieveToken,
  decodeToken,
  roleFlagsFromToken,
} from '@/utils/authentication';
import { useApi } from '@/context/Api';

interface AuthenticationContextData {
  initialized: boolean;
  user?: UserInfo;
  hasPhonicsAccess: boolean;
  studentClassId?: string;
  storeToken: (newToken: string | null) => void;
  token: string | null;
  logout: () => void;
}

const initialValue: AuthenticationContextData = {
  initialized: false,
  user: {
    countryCode: '',
    hasRecordingConsent: false,
    id: '',
    isAdmin: false,
    isAIPowerUser: false,
    isAllowedToUseAI: false,
    isParent: false,
    isQA: false,
    isSalesUser: false,
    isSchoolAdmin: false,
    isStudent: false,
    isTeacher: false,
    isPortalUser: false,
    name: '',
    provider: '',
    role: '',
    email: '',
  },
  hasPhonicsAccess: false,
  storeToken: () => undefined,
  token: null,
  logout: () => undefined,
};

const AuthenticationContext =
  createContext<AuthenticationContextData>(initialValue);

const hasGlobalUserRole = (user: UserInfo) => user.isAdmin || user.isSalesUser;

const hasAtLeastOnePhonicsFlag = (user: GetFlagsForUserQuery['user']) => {
  switch (user.__typename) {
    case 'TeacherSummaryType':
      return user.institutions
        .flatMap((institution) =>
          institution.classes.flatMap((klass) => klass.flags),
        )
        .some((flagAssignment) => flagAssignment.flag.name === 'PHONICS');

    case 'StudentType':
      return user.classes
        .flatMap((klass) => klass.flags)
        .some((flagAssignment) => flagAssignment.flag.name === 'PHONICS');
    default:
      return false;
  }
};

export const AuthenticationContextProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const { setToken: setApiToken, initialized: apiInitialized } = useApi();

  const [initialized, setInitialized] = useState<boolean>(false);

  const [tokenChecked, setTokenChecked] = useState<boolean>(false);

  const [token, setToken] = useState<string | null>(null);

  const [user, setUser] = useState<UserInfo | undefined>();

  const [hasPhonicsAccess, setHasPhonicsAccess] = useState<boolean>(false);

  const [studentClassId, setStudentClassId] = useState<string | undefined>();

  const { data: userFlagsResult } = useGetFlagsForUserQuery({
    skip: !apiInitialized,
  });

  const logout = useCallback(() => {
    storeToken(null);
    setToken(null);
    setApiToken(null);
    setUser(undefined);
  }, [setToken, setApiToken]);

  useEffect(() => {
    (async () => {
      const rawToken = await retrieveToken();

      setToken(rawToken);
      setApiToken(rawToken);
      setTokenChecked(true);
    })();
  }, [setApiToken]);

  useEffect(() => {
    if (!tokenChecked) {
      return;
    }

    if (!token) {
      setUser(undefined);
      setInitialized(true);

      return;
    }

    const decodedToken = decodeToken(token);

    if (!decodedToken) {
      logout();
      setInitialized(true);

      return;
    }

    const roleFlags = roleFlagsFromToken(decodedToken);

    setUser({
      ...decodedToken,
      ...roleFlags,
    });
  }, [logout, tokenChecked, token]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!userFlagsResult?.user) {
      return;
    }

    setHasPhonicsAccess(
      hasGlobalUserRole(user) || hasAtLeastOnePhonicsFlag(userFlagsResult.user),
    );

    if (user.isStudent) {
      setStudentClassId(
        userFlagsResult.user.__typename === 'StudentType'
          ? userFlagsResult.user.classes?.[0].id
          : undefined,
      );
    }

    setInitialized(true);
  }, [user, userFlagsResult]);

  return (
    initialized && (
      <AuthenticationContext.Provider
        value={{
          initialized,
          user,
          hasPhonicsAccess,
          studentClassId,
          storeToken: (newToken: string | null) => {
            storeToken(newToken);
            setToken(newToken);
            setApiToken(newToken);
          },
          token,
          logout,
        }}
      >
        {children}
      </AuthenticationContext.Provider>
    )
  );
};

export const useAuth = () => useContext(AuthenticationContext);

export const useNonStudentRedirect = () => {
  const router = useRouter();
  const { user } = useAuth();

  useEffect(() => {
    if (user && !user.isStudent) {
      router.push('/manage');
    }
  }, [user, router]);
};

export const useNotLoggedInRedirect = () => {
  const router = useRouter();
  const { user, hasPhonicsAccess } = useAuth();

  useEffect(() => {
    if (!user) {
      router.push('/join');

      return;
    }

    if (!hasPhonicsAccess) {
      router.push('/no-access');
    }
  }, [user, hasPhonicsAccess, router]);
};

export const withAuth = (
  WrappedComponent: React.ComponentType,
  allowedRoles: RoleFlagOptions = {
    isAdmin: false,
    isParent: false,
    isQA: false,
    isSalesUser: false,
    isSchoolAdmin: false,
    isStudent: false,
    isTeacher: false,
    isPortalUser: false,
  },
) => {
  const ComponentWithAuth = () => {
    const router = useRouter();
    const { initialized, hasPhonicsAccess, user } = useAuth();

    const userHasRole = userHasRequiredRole(user, allowedRoles);

    useEffect(() => {
      if (!initialized) {
        return;
      }

      if (!user) {
        router.push('/join');
      } else if (!userHasRole || !hasPhonicsAccess) {
        router.push('/no-access');
      }
    }, [initialized, user, userHasRole, hasPhonicsAccess, router]);

    if (!initialized || !user || !userHasRole || !hasPhonicsAccess) {
      return <Loading />;
    }

    return <WrappedComponent />;
  };

  ComponentWithAuth.displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithAuth;
};
